import React, { useState, useEffect } from 'react';

function DataGrid({ profileSpacing, numberOfReadings = 10, readingType = 'depth', onProfileMinChange, onProfileMaxChange, onPositionsChange, onDepthsChange, initialData }) {
  const [data, setData] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize data with saved data or empty rows
  useEffect(() => {
    if (initialData && initialData.positions && initialData.depths && !isInitialized) {
      // Load saved data
      const newData = [];
      for (let i = 0; i < Math.max(initialData.positions.length, numberOfReadings); i++) {
        newData.push({
          position: initialData.positions[i] !== undefined ? initialData.positions[i].toString() : '',
          value: initialData.depths[i] !== undefined ? initialData.depths[i].toString() : ''
        });
      }
      // Pad with empty rows if needed
      while (newData.length < numberOfReadings) {
        newData.push({ position: '', value: '' });
      }
      setData(newData);
      setIsInitialized(true);
    } else if (!isInitialized) {
      // Initialize with empty rows
      setData(Array.from({ length: numberOfReadings }, () => ({
        position: '',
        value: ''
      })));
      setIsInitialized(true);
    }
  }, [initialData, numberOfReadings, isInitialized]);

  // Adjust number of rows after initialization
  useEffect(() => {
    if (!isInitialized) return;
    
    setData(prevData => {
      if (prevData.length < numberOfReadings) {
        return [
          ...prevData,
          ...Array.from({ length: numberOfReadings - prevData.length }, () => ({
            position: '',
            value: ''
          }))
        ];
      }

      if (prevData.length > numberOfReadings) {
        return prevData.slice(0, numberOfReadings);
      }

      return prevData;
    });
  }, [numberOfReadings, isInitialized]);

  // Update positions when profileSpacing changes
  useEffect(() => {
    if (profileSpacing !== undefined && profileSpacing !== '') {
      const spacing = parseFloat(profileSpacing) || 0;
      setData(prevData =>
        prevData.map((row, index) => ({
          ...row,
          position: (index * spacing).toString()
        }))
      );
    }
  }, [profileSpacing]);

  // Report the minimum and maximum valid values in the second column
  useEffect(() => {
    if (!onProfileMinChange) return;
    if (!onProfileMaxChange) return;
    const numericValues = data
      .map(row => parseFloat(row.value))
      .filter(value => !Number.isNaN(value));

    const minValue = numericValues.length > 0
      ? Math.min(...numericValues).toFixed(2)
      : '';

    const maxValue = numericValues.length > 0
      ? Math.max(...numericValues).toFixed(2)
      : ''; 

    onProfileMinChange(minValue);
    onProfileMaxChange(maxValue);
  }, [data, onProfileMinChange, onProfileMaxChange]);

  // Expose Positions and Depths as separate arrays
  useEffect(() => {
    const positions = data
      .map(row => parseFloat(row.position))
      .filter(value => !Number.isNaN(value));
    
    const depths = data
      .map(row => parseFloat(row.value))
      .filter(value => !Number.isNaN(value));

    if (onPositionsChange) {
      onPositionsChange(positions);
    }

    if (onDepthsChange) {
      onDepthsChange(depths);
    }
  }, [data, onPositionsChange, onDepthsChange]);

  const handleCellChange = (rowIndex, field, value) => {
    const newData = [...data];
    newData[rowIndex][field] = value;
    setData(newData);
  };

  return (
    <div style={{
      height: 200,
      width: 300,
      border: '1px solid #ddd',
      borderRadius: '4px',
      overflow: 'auto',
      fontFamily: 'Arial, sans-serif',
      fontSize: '14px'
    }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        backgroundColor: 'white'
      }}>
        <thead>
          <tr style={{ backgroundColor: '#f8f8f8' }}>
            <th style={{
              border: '1px solid #ddd',
              padding: '8px 12px',
              textAlign: 'left',
              fontWeight: 'bold',
              backgroundColor: '#f8f8f8',
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}>
              Position
            </th>
            <th style={{
              border: '1px solid #ddd',
              padding: '8px 12px',
              textAlign: 'left',
              fontWeight: 'bold',
              backgroundColor: '#f8f8f8',
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}>
              {readingType === 'thickness' ? 'Remaining Thickness' : 'Depth'}
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index} style={{
              backgroundColor: index % 2 === 0 ? 'white' : '#f9f9f9'
            }}>
              <td style={{
                border: '1px solid #ddd',
                padding: '0'
              }}>
                <input
                  type="text"
                  value={row.position}
                  tabIndex={index + 1}
                  onChange={(e) => handleCellChange(index, 'position', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                  placeholder=""
                />
              </td>
              <td style={{
                border: '1px solid #ddd',
                padding: '0'
              }}>
                <input
                  type="text"
                  value={row.value}
                  tabIndex={numberOfReadings + index + 1}
                  onChange={(e) => handleCellChange(index, 'value', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                  placeholder=""
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default DataGrid;
